import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Sparkles } from "lucide-react";
import { useRef } from "react";
import { LogoMark } from "./Logo";

const lineVariants = {
  hidden: { y: "110%", opacity: 0 },
  visible: (i) => ({
    y: "0%",
    opacity: 1,
    transition: { duration: 0.9, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-32 pb-16">
      <motion.div style={{ y: yBg }} className="absolute inset-0 hero-grid-lines" aria-hidden="true" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 20%, #162248 0%, #0B132B 55%, #060B19 100%)" }}
        aria-hidden="true"
      />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full blur-[140px] opacity-20 bg-[#003366]" aria-hidden="true" />
      <div className="absolute top-1/3 -right-32 w-[420px] h-[420px] rounded-full blur-[140px] opacity-15 bg-[#D4AF37]" aria-hidden="true" />

      <motion.div
        style={{ y: yLogo }}
        className="absolute left-4 sm:left-16 top-1/4 opacity-[0.07] pointer-events-none float-slow"
        aria-hidden="true"
      >
        <LogoMark size={320} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="num-badge rounded-full px-4 py-1.5 text-[11px] sm:text-xs text-[#E5C158]" data-testid="hero-kicker">
            مكتب براح الرقمية للاتصالات وتقنية المعلومات
          </span>
          <Sparkles size={16} className="text-[#D4AF37]" />
        </motion.div>

        <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.9] sm:leading-[1.8] tracking-normal" data-testid="hero-headline">
          {["نبني البنية الرقمية للمستقبل", "ونطلق المنصات الذكية"].map((line, i) => (
            <span key={i} className="block overflow-hidden py-3">
              <motion.span
                className={`block ${i === 1 ? "gold-text" : "text-[#F8FAFC]"}`}
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-6 max-w-2xl font-body text-base sm:text-lg leading-relaxed text-[#94A3B8]"
          data-testid="hero-subtext"
        >
          من الفكرة إلى الإطلاق — نصمّم ونطوّر منصات ومواقع مدعومة بالذكاء الاصطناعي،
          ونقود مشاريع التحول الرقمي للمنشآت بخبرة تمتد لأكثر من 18 عاماً في الأنظمة المالية والمؤسسية والاتصالات.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#contact");
              if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
            }}
            data-testid="hero-cta-primary"
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 font-display font-bold text-[#060B19] hover:bg-[#E5C158] hover:shadow-[0_0_40px_rgba(212,175,55,0.35)] transition-all duration-300"
          >
            اطلب استشارة مجانية
          </a>
          <a
            href="#platforms"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#platforms");
              if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
            }}
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-8 py-3.5 font-display font-bold text-[#E5C158] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300"
          >
            اكتشف منصاتنا
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#D4AF37]/60"
        aria-hidden="true"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ChevronDown size={26} />
        </motion.div>
      </motion.div>
    </section>
  );
};
